import React, { useEffect, useState } from 'react';
import { Box, ClickAwayListener, Grow, Paper, Popper, TextField } from '@mui/material';
import { Chrome, color as parseColor, type ColorResult } from '@uiw/react-color';

import {
  agentSelectorColorFieldRowSx,
  agentSelectorColorSwatchSx,
  agentSelectorColorTextFieldSx,
} from '../agentSelectorThemeFieldStyles';

const normalizeHex = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  return null;
};

type Props = {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  dataTestId?: string;
};

export const EllaAgentSelectorColorField = ({
  label,
  value,
  onChange,
  disabled = false,
  dataTestId,
}: Props): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = useState(value);
  const colorResult = parseColor(value || '#000000');

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const applyColor = (next: ColorResult) => {
    const hex = next.hex.toUpperCase();
    setInputValue(hex);
    onChange(hex);
  };

  return (
    <Box data-testid={dataTestId} sx={agentSelectorColorFieldRowSx}>
      <TextField
        label={label}
        value={inputValue}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value;
          setInputValue(next);
          const normalized = normalizeHex(next);
          if (normalized) {
            onChange(normalized);
          }
        }}
        InputLabelProps={{ shrink: true }}
        sx={agentSelectorColorTextFieldSx}
        inputProps={{ 'aria-label': label }}
      />
      <Box
        component="button"
        type="button"
        aria-label={`${label} preview`}
        disabled={disabled}
        onClick={(event) => {
          if (!disabled) {
            setAnchorEl(event.currentTarget);
          }
        }}
        sx={{
          ...agentSelectorColorSwatchSx,
          bgcolor: colorResult.hex,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      />
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Grow {...TransitionProps} style={{ transformOrigin: '0 0 0' }} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  overflow: 'hidden',
                  bgcolor: '#FFFFFF',
                  '& .w-color-chrome': { backgroundColor: '#FFFFFF' },
                }}
              >
                <Chrome
                  color={colorResult.hsva}
                  onChange={applyColor}
                  style={{ backgroundColor: '#FFFFFF', boxShadow: 'none' }}
                />
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
};
